import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';

/**
 * Shown when a new registration uses an ABHA number that already belongs to a
 * patient. The patient keeps one UHID; the new ABHA address (if any) is added
 * to that patient instead of creating a duplicate.
 */
export default function ExistingAbhaPatientDialog({ open, patient, newAbhaAddress, onCancel, onConfirm, submitting }) {
  const [makePreferred, setMakePreferred] = useState(false);

  useEffect(() => {
    if (open) setMakePreferred(false);
  }, [open]);

  if (!patient) return null;

  const knownAddresses = (patient.abha_addresses || []).map((item) => item.abha_address);
  const normalizedNew = (newAbhaAddress || '').trim().toLowerCase();
  const isNewAddress = Boolean(normalizedNew) && !knownAddresses.includes(normalizedNew);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Patient already registered</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Alert severity="info">
            This ABHA number is already registered to the patient below. No new patient will be created; the visit continues under the
            same UHID.
          </Alert>
          <Typography variant="body1">
            <strong>UHID:</strong> {patient.id}
          </Typography>
          <Typography variant="body1">
            <strong>Name:</strong> {patient.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gender: {patient.gender || 'N/A'} | DOB: {patient.dob || 'N/A'} | Mobile: {patient.phone_number || 'N/A'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="body2">
              <strong>ABHA addresses:</strong>
            </Typography>
            {knownAddresses.length === 0 && <Typography variant="body2">None</Typography>}
            {(patient.abha_addresses || []).map((item) => (
              <Chip
                key={item.abha_address}
                size="small"
                label={item.is_preferred ? `${item.abha_address} (default)` : item.abha_address}
                color={item.is_preferred ? 'success' : 'default'}
              />
            ))}
          </Stack>
          {isNewAddress && (
            <>
              <Typography variant="body1">
                New ABHA address <strong>{normalizedNew}</strong> will be added to this patient.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={makePreferred} onChange={(e) => setMakePreferred(e.target.checked)} />}
                label="Use this as the default ABHA address for ABDM record linking"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="error" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm({ addAddress: isNewAddress, makePreferred })}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isNewAddress ? 'Add address & continue with this patient' : 'Continue with this patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ExistingAbhaPatientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  patient: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    gender: PropTypes.string,
    dob: PropTypes.string,
    phone_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    abha_addresses: PropTypes.arrayOf(
      PropTypes.shape({ abha_address: PropTypes.string, is_preferred: PropTypes.bool })
    )
  }),
  newAbhaAddress: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  submitting: PropTypes.bool
};
